import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from "react";
import classNames from "classnames";
import { getUser, LoyaltyApi } from "@jti/rest-api";
import { Button } from "../../../../components/Button";
import { DEPRECATED_Typography } from "../../../../components/DEPRECATED_Typography";
import { Icon } from "../../../../components/Icon";
import { Loading } from "../../../../components/Loading";
import { VirtualPrizeBadges } from "../../../../components/VirtualPrizeBadges";
import { usePagination } from "../../../../hooks/usePagination/usePagination";
import Arrow from "../../../../icons/arrow.svg";
import { replaceStringPlaceholder } from "../../../../utils/replaceStringPlaceholder/replaceStringPlaceholder";
import { validateLimitedTimeBadgesData } from "../../../../utils/validateBadgeData/validateBadgeData";
import { usePrizeLocatorContext } from "../../hooks";
import { GOOGLE_DIRECTION_URL_CONST } from "../../PrizeLocator";
import { PrizeTile } from "../PrizeTile/PrizeTile";
import styles from "./PrizesList.module.scss";
var LoadingStatus;
(function (LoadingStatus) {
    LoadingStatus[LoadingStatus["INITIAL_LOAD"] = 0] = "INITIAL_LOAD";
    LoadingStatus[LoadingStatus["LOADING"] = 1] = "LOADING";
    LoadingStatus[LoadingStatus["SUCCESS"] = 2] = "SUCCESS";
    LoadingStatus[LoadingStatus["FAILED"] = 3] = "FAILED";
})(LoadingStatus || (LoadingStatus = {}));
const validateTierRestriction = (tier, levels) => {
    return tier && levels?.length ? !levels.some((level) => level.toLowerCase() === tier.toLowerCase()) : false;
};
const isRewardAvailable = (isLogged, pointsBalance, rewardCost, tier, levels, hasCoupons) => isLogged && rewardCost <= pointsBalance && !validateTierRestriction(tier, levels) && hasCoupons;
const isLimitedOfferEnabled = (timeTo, oneDayLabel, daysLabel) => {
    return !!timeTo && !!oneDayLabel && !!daysLabel;
};
export const PrizesList = ({ stores, activeStoreId, onStoreSelect, displayType }) => {
    const [prizes, setPrizes] = useState({
        items: [],
        totalItems: 0,
    });
    const [prizePageNo, setPrizePageNo] = useState(1);
    const [loadingStatus, setLoadingStatus] = useState(LoadingStatus.INITIAL_LOAD);
    const [isOLv4, setIsOLv4] = useState(false);
    const [prev, setPrev] = useState({
        activeStoreId: undefined,
        pageNo: undefined,
    });
    const { labels: { noOffersText, rewardsText, backToListLabel, loadMoreLabel, pointsLabel, newOfferDays, newLabel, oneDayLabel, daysLabel, outOfStockLabel, }, handleRedeemClick, userPointsBalance, pagination, tier, } = usePrizeLocatorContext();
    const [paginatedItems, paginationLoadMore, paginationReset] = usePagination(prizes.items, pagination);
    const user = getUser();
    const isLogged = !!(user?.uuid && user?.ciam_token);
    const isMapView = displayType === "map";
    const activeStoreData = stores.find((store) => store.ExternalId === activeStoreId);
    const fetchPrizes = () => {
        paginationReset();
        LoyaltyApi.init()
            .getPrizes({
            page: prizePageNo,
            perPage: pagination,
            sort: "asc",
            orderBy: "points",
            ...(activeStoreData?.ExternalId ? { location_ids: activeStoreData.ExternalId } : { is_virtual: 0 }),
        })
            .then((response) => {
            let newPrizes;
            if (loadingStatus === LoadingStatus.INITIAL_LOAD) {
                newPrizes = { items: [...response.items], totalItems: response.total };
                setIsOLv4(response.items.length === response.total);
            }
            else {
                newPrizes = { ...prizes, items: [...prizes.items, ...response.items] };
            }
            setPrizes(newPrizes);
            setLoadingStatus(LoadingStatus.SUCCESS);
        })
            .catch((response) => {
            setLoadingStatus(LoadingStatus.FAILED);
            console.error(response);
        });
    };
    if (prev.pageNo !== prizePageNo && prizePageNo > 1) {
        setPrev({ ...prev, pageNo: prizePageNo });
        setLoadingStatus(LoadingStatus.LOADING);
    }
    if (prev.activeStoreId !== activeStoreData?.ExternalId) {
        setPrev({ ...prev, activeStoreId: activeStoreData?.ExternalId });
        setPrizePageNo(1);
        setLoadingStatus(LoadingStatus.INITIAL_LOAD);
    }
    useEffect(() => {
        if (loadingStatus === LoadingStatus.INITIAL_LOAD || loadingStatus === LoadingStatus.LOADING)
            fetchPrizes();
    }, [loadingStatus]);
    //fixme after full migration to OLv5 code below neeeds to be removed;
    const prizesToDisplay = isOLv4 ? { items: paginatedItems, totalItems: prizes.totalItems } : { ...prizes };
    const handleLoadMore = () => {
        if (isOLv4) {
            paginationLoadMore();
        }
        else {
            setPrizePageNo(prizePageNo + 1);
        }
    };
    const handleBackToListClick = () => {
        onStoreSelect("");
    };
    const prizesCount = replaceStringPlaceholder(rewardsText, [{ placeholder: "count", value: prizes.totalItems }], false);
    return (_jsx("div", { className: styles.wrapper, "data-testid": "storeLocatorCardList", id: "storeLocatorCardList", "data-prizescount": prizes.totalItems, children: loadingStatus === LoadingStatus.INITIAL_LOAD ? (_jsx(Loading, { dataTestId: "prizeListLoader", className: styles.loader })) : (_jsxs("div", { className: styles.prizesList, children: [activeStoreData && (_jsxs("div", { className: classNames(styles.venueDetails, {
                        [styles.mapView]: isMapView,
                    }), children: [_jsxs(Button, { "data-testid": "prizeListBackButton", color: "tertiary", onClick: handleBackToListClick, className: styles.backButton, children: [_jsx(Icon, { component: Arrow, rotate: 270 }), backToListLabel] }), _jsx("div", { className: styles.venueName, children: activeStoreData.StoreName }), _jsx(DEPRECATED_Typography, { type: "p3", weight: "medium", color: "black", children: prizesCount }), _jsx(DEPRECATED_Typography, { type: "p3", color: "black", className: styles.description, children: activeStoreData.Description })] })), !prizesToDisplay.items?.length ? (_jsx(DEPRECATED_Typography, { type: "p3", color: "black", className: styles.noOffers, children: noOffersText })) : (_jsxs(_Fragment, { children: [_jsx("div", { "data-testid": "listItems", className: styles.prizes, children: prizesToDisplay.items?.map((item) => {
                                const hasLimitedOfferBadge = isLimitedOfferEnabled(item.time_to, oneDayLabel, daysLabel);
                                const isTierRestricted = validateTierRestriction(tier, item.levels);
                                const { isNewBadgeVisible, isMoreThanOneDay, limitedTimeOfferLabel } = validateLimitedTimeBadgesData({
                                    newOfferDays,
                                    timeFrom: item.time_from,
                                    timeTo: item.time_to,
                                    daysLabel,
                                    oneDayLabel,
                                });
                                const lowestEligibleTier = item.levels?.[0];
                                const color = item.color === "black" ? "black" : "white";
                                return (_jsx(PrizeTile, { title: item.name, venueName: item.brandName, description: item.subtitle, getDirectionUrl: `${GOOGLE_DIRECTION_URL_CONST}${activeStoreData?.Location?.lat},${activeStoreData?.Location?.lon}`, imageUrl: item.images[0], points: `${item.points} ${pointsLabel}`, isAvailable: isRewardAvailable(isLogged, userPointsBalance, item.points, tier, item.levels, item.has_coupons), handleRedeemClick: () => handleRedeemClick(item), handleVenueClick: () => onStoreSelect(item.locations[0]), children: _jsx(VirtualPrizeBadges, { showNewBadge: isNewBadgeVisible, isTierRestricted: isTierRestricted || !isLogged, tier: lowestEligibleTier, isOutOfStock: item.has_coupons === false, hasLimitedOfferBadge: hasLimitedOfferBadge, newBadgeLabel: newLabel, timeLimitedBadgeLabel: limitedTimeOfferLabel, isMoreThanOneDay: isMoreThanOneDay, color: color, outOfStockLabel: outOfStockLabel, isPlacedInSmallBox: true }) }, item.prize_uuid));
                            }) }), prizes.totalItems > prizesToDisplay.items?.length &&
                            (loadingStatus === LoadingStatus.LOADING ? (_jsx(Loading, {})) : (_jsx(Button, { onClick: handleLoadMore, color: "secondary", children: loadMoreLabel })))] }))] })) }));
};
//# sourceMappingURL=PrizesList.js.map